import React from 'react';
import { StyleSheet, View, TouchableOpacity, Linking, Platform } from 'react-native';
import { ODSText } from '../../atoms/ODSText';
import { getTokens } from '../../../design-tokens/color-tokens';
import getScreenType from '../../../utils/getScreenType';
import { ODSIcon } from '../../atoms/ODSIcon';
import moment from 'moment';
const howToRentGuide = 'https://assets.openbrix.co.uk/userdoc-4ofHm-1775844247656-0.pdf';

const NoticeBox = ({ data, restStyle }: any) => {
    const themeObj = getTokens('light').colors;

    const resolvedData = data?.data || data;
    const noticeDocs = resolvedData?.open_flow_move_out_notice_documents || [];
    const noticeDetails = resolvedData?.open_flow_move_out_notice;
    const openFlow = resolvedData?.open_flow;

    const tenancyDocs = [
        {
            title: "The Renters Rights Act 2026",
            date: resolvedData?.createdAt ? moment(resolvedData.createdAt).format("DD/MM/YYYY") : "02/05/2024",
            url: openFlow?.how_to_rent_pdf_url || howToRentGuide
        },
        openFlow?.prescribed_contract_url ? {
            title: "Prescribed Info",
            date: resolvedData?.createdAt ? moment(resolvedData.createdAt).format("DD/MM/YYYY") : "02/05/2024",
            url: openFlow?.prescribed_contract_url
        } : null,
        {
            title: "Tenancy Agreement",
            date: resolvedData?.createdAt ? moment(resolvedData.createdAt).format("DD/MM/YYYY") : "02/05/2024",
            url: openFlow?.ast_contract_signed_url || openFlow?.ast_contract_url || openFlow?.draft_agreement_url,
        },
    ].filter(Boolean);

    const openDocUrl = (url?: string) => {
        if (!url) return;
        if (Platform.OS === 'web') {
            window.open(url, '_blank', 'noreferrer');
        } else {
            Linking.openURL(url);
        }
    };

    const servedDocs = noticeDocs.filter((d: any) => d.document_type === 'served');
    const receivedDocs = noticeDocs.filter((d: any) => d.document_type === 'received');
    const otherDocs = noticeDocs.filter((d: any) => d.document_type !== 'served' && d.document_type !== 'received');

    const renderDocCard = (doc: any, index: number) => {
        const isServed = doc?.document_type === 'served';
        const isReceived = doc?.document_type === 'received';
        const docTitle = isServed ? 'Notice Served' : isReceived ? 'Notice Received' : 'Notice';
        const statusColor = isServed ? '#1FA6B2' : '#F44D3F';
        const statusText = isServed ? 'Served' : 'Received';

        return (
            <TouchableOpacity
                key={doc?.open_flow_move_out_notice_document_id || index}
                onPress={() => openDocUrl(doc?.file_url)}
                style={styles(themeObj).docCard}
            >
                <View style={styles(themeObj).downloadIconBtn}>
                    <ODSIcon icon="TB-Download" size={24} color={themeObj.button.main} />
                </View>

                <View style={styles(themeObj).docTextContainer}>
                    <ODSText type="md-bold" color={themeObj.text.primary}>
                        {docTitle}
                    </ODSText>
                    <ODSText type="sm" color={themeObj.text.secondary} marginTop={4}>
                        {moment(doc?.createdAt || noticeDetails?.notice_date || data?.createdAt).format("DD/MM/YYYY")}
                    </ODSText>
                </View>

                <View style={styles(themeObj).statusContainer}>
                    <ODSIcon icon="TBDoc" size={20} color={statusColor} />
                    <ODSText type="xs" color={statusColor} marginTop={2}>
                        {statusText}
                    </ODSText>
                </View>
            </TouchableOpacity>
        );
    };

    return (
        <View style={[styles(themeObj).containerStyle, restStyle]}>
            {/* Section 1: Notice */}
            <ODSText
                textAlign="flex-start"
                color="black"
                type="h4"
            >
                Notice
            </ODSText>

            <ODSText
                textAlign="flex-start"
                marginTop={4}
                marginBottom={24}
                color={themeObj.text.secondary}
                type="sm"
            >
                {noticeDetails?.notice_date
                    ? `Notice has been served. Expected move out date is ${moment(noticeDetails.move_out_date).format("DD/MM/YYYY")}.`
                    : "No notice has been served yet."}
            </ODSText>

            {/* Notice Details Section */}
            {noticeDetails && (
                <View style={{ marginBottom: 24 }}>
                    <ODSText textAlign="flex-start" color={themeObj.text.primary} type="md-bold" marginBottom={12}>
                        Notice Details
                    </ODSText>
                    <View style={styles(themeObj).detailsGrid}>
                        {noticeDetails?.notice_date && (
                            <View style={styles(themeObj).detailCol}>
                                <ODSText type="sm-bold" color={themeObj.text.secondary}>Notice Date</ODSText>
                                <ODSText type="md" color="black" marginTop={4}>
                                    {moment(noticeDetails.notice_date).format("DD/MM/YYYY")}
                                </ODSText>
                            </View>
                        )}
                        {noticeDetails?.move_out_date && (
                            <View style={styles(themeObj).detailCol}>
                                <ODSText type="sm-bold" color={themeObj.text.secondary}>Move Out Date</ODSText>
                                <ODSText type="md" color="black" marginTop={4}>
                                    {moment(noticeDetails.move_out_date).format("DD/MM/YYYY")}
                                </ODSText>
                            </View>
                        )}
                        {noticeDetails?.liability_date && (
                            <View style={styles(themeObj).detailCol}>
                                <ODSText type="sm-bold" color={themeObj.text.secondary}>Liability Date</ODSText>
                                <ODSText type="md" color="black" marginTop={4}>
                                    {moment(noticeDetails.liability_date).format("DD/MM/YYYY")}
                                </ODSText>
                            </View>
                        )}
                        <View style={styles(themeObj).detailCol}>
                            <ODSText type="sm-bold" color={themeObj.text.secondary}>Notice Received</ODSText>
                            <ODSText type="md" color={noticeDetails?.notice_received_completed ? '#1FA6B2' : '#F44D3F'} marginTop={4}>
                                {noticeDetails?.notice_received_completed ? 'Completed' : 'Pending'}
                            </ODSText>
                        </View>
                        <View style={styles(themeObj).detailCol}>
                            <ODSText type="sm-bold" color={themeObj.text.secondary}>Notice Served</ODSText>
                            <ODSText type="md" color={noticeDetails?.notice_served_completed ? '#1FA6B2' : '#F44D3F'} marginTop={4}>
                                {noticeDetails?.notice_served_completed ? 'Completed' : 'Pending'}
                            </ODSText>
                        </View>
                    </View>
                </View>
            )}

            {/* Subsection: Served Documents */}
            {servedDocs.length > 0 && (
                <View style={{ marginBottom: 16 }}>
                    <ODSText textAlign="flex-start" color={themeObj.text.primary} type="md-bold" marginBottom={12}>
                        Notice Served Documents
                    </ODSText>
                    {servedDocs.map((doc: any, index: number) => renderDocCard(doc, index))}
                </View>
            )}

            {/* Subsection: Received Documents */}
            {receivedDocs.length > 0 && (
                <View style={{ marginBottom: 16 }}>
                    <ODSText textAlign="flex-start" color={themeObj.text.primary} type="md-bold" marginBottom={12}>
                        Notice Received Documents
                    </ODSText>
                    {receivedDocs.map((doc: any, index: number) => renderDocCard(doc, index))}
                </View>
            )}

            {/* Subsection: Other Notice Documents */}
            {otherDocs.length > 0 && (
                <View style={{ marginBottom: 16 }}>
                    <ODSText textAlign="flex-start" color={themeObj.text.primary} type="md-bold" marginBottom={12}>
                        Notice Documents
                    </ODSText>
                    {otherDocs.map((doc: any, index: number) => renderDocCard(doc, index))}
                </View>
            )}


            {/* Divider */}
            <View style={styles(themeObj).divider} />

            {/* Section 2: Tenancy information */}
            <ODSText
                textAlign="flex-start"
                color="black"
                type="h4"
            >
                Tenancy information
            </ODSText>

            <ODSText
                textAlign="flex-start"
                marginTop={4}
                marginBottom={24}
                color={themeObj.text.secondary}
                type="sm"
            >
                {openFlow?.property?.property_name
                    ? `Tenancy details and documentation for ${openFlow.property.property_name}.`
                    : "Tenancy details and documents."}
            </ODSText>

            {tenancyDocs.map((item, index) => (
                <TouchableOpacity
                    key={index}
                    onPress={() => openDocUrl(item.url)}
                    style={styles(themeObj).docCard}
                >
                    <View style={styles(themeObj).downloadIconBtn}>
                        <ODSIcon icon="TB-Download" size={24} color={themeObj.button.main} />
                    </View>

                    <View style={styles(themeObj).docTextContainer}>
                        <ODSText type="md-bold" color={themeObj.text.primary}>
                            {item.title}
                        </ODSText>
                        <ODSText type="sm" color={themeObj.text.secondary} marginTop={4}>
                            {item.date}
                        </ODSText>
                    </View>
                </TouchableOpacity>
            ))}
        </View>
    );
};

const styles = (theme: any) =>
    StyleSheet.create({
        containerStyle: {
            width: getScreenType() == 'web' ? 840 : "100%",
            borderRadius: 12,
            shadowColor: '#101828',
            shadowOffset: { width: 0, height: 1 },
            shadowOpacity: 0.1,
            shadowRadius: 2,
            elevation: 2,
            backgroundColor: theme.neutral['white'],
            paddingVertical: 32,
            paddingHorizontal: 24,
            borderWidth: 1,
            borderColor: '#D0D6DD',
            alignSelf: 'center',
            marginBottom: 100,
        },
        docCard: {
            flexDirection: 'row',
            alignItems: 'center',
            borderWidth: 1,
            borderColor: '#E5E7EB',
            borderRadius: 12,
            paddingHorizontal: 20,
            paddingVertical: 16,
            backgroundColor: '#FFFFFF',
            marginBottom: 16,
        },
        downloadIconBtn: {
            marginRight: 4,
        },
        docTextContainer: {
            flex: 1,
            marginLeft: 12,
        },
        statusContainer: {
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            marginLeft: 12,
        },
        divider: {
            height: 1,
            backgroundColor: '#E5E7EB',
            width: '100%',
            marginVertical: 24,
        },
        detailsGrid: {
            flexDirection: 'row',
            flexWrap: 'wrap',
            marginBottom: 8,
            backgroundColor: '#F9FAFB',
            borderRadius: 8,
            padding: 16,
            borderWidth: 1,
            borderColor: '#E5E7EB',
        },
        detailCol: {
            width: getScreenType() === 'web' ? '33%' : '50%',
            marginBottom: 16,
            paddingRight: 8,
        },
    });

export default NoticeBox;
